import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/* ---------- GLOBAL LAYOUT ---------- */
import Header from "./components/Header";
import Footer from "./components/Footer";

/* ---------- HOME SECTIONS ---------- */
import { Hero } from "./components/Hero";
import InstitutionalCTA from "./components/InstitutionalCTA";
import { HealthImpact } from "./components/HealthImpact";
import { Products } from "./components/Products";
import { Features } from "./components/Features";
import { Testimonials } from "./components/Testimonials";
import { InterestForm } from "./components/InterestForm";
import AQSenseTeaser from "./components/AQSenseTeaser";

import { Toaster } from "./components/ui/sonner";

/* ---------- SUPPORT / PHASE ---------- */
import PreorderCounter from "./components/PreorderCounter";
import LaunchTimeline from "./components/LaunchTimeline";
import FAQ from "./components/FAQ";
import AppTeaser from "./components/AppTeaser";
import EarlyAccessCountdown from "./components/EarlyAccessCountdown";
import SocialProof from "./components/SocialProof";
import ServiceWarranty from "./components/ServiceWarranty";
import Compliance from "./components/Compliance";
import ManufacturingStory from "./components/ManufacturingStory";
import GovtAirAdvisory from "./components/GovtAirAdvisory";

/* ---------- PAGES ---------- */
import Terms from "./pages/terms";
import Refund from "./pages/refund";
import Shipping from "./pages/shipping";
import Privacy from "./pages/privacy";
import Contact from "./pages/contact";

/* ---------- STRATEGIC / INSTITUTIONAL ---------- */
import InstitutionalSolutions from "./pages/InstitutionalSolutions";
import DealerEnquiry from "./pages/DealerEnquiry";
import MAFA from "./pages/MAFA";
import AirShieldTransitZone from "./pages/AirShieldTransitZone";
import Vision from "./pages/Vision";
import Mission from "./pages/Mission";
import EnvironmentalResponsibility from "./pages/EnvironmentalResponsibility";

/* ---------- AQ SENSE ---------- */
import AQSense from "./pages/AQSense";

/* ---------- PREBOOK ---------- */
import PrebookCheckout from "./pages/PrebookCheckout";

/* ---------- LAZY ---------- */
const DiwaliProtection = React.lazy(() =>
  import("./components/DiwaliProtection")
);

/* ---------- LOADER ---------- */
const Loader = ({ height = 350 }) => (
  <div
    className="w-full flex items-center justify-center"
    style={{ height }}
  >
    <div className="text-sm text-gray-500">Loading…</div>
  </div>
);

/* ======================================================
   SHARED PAGE LAYOUT
====================================================== */
function PageLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

/* ======================================================
   HOME
====================================================== */
function Home() {
  return (
    <PageLayout>
      <Helmet>
        <title>Air Purifiers for Indian Air | AERZEN AIR™</title>
        <meta
          name="description"
          content="AERZEN AIR™ designs high-performance air purification systems for Indian AQI conditions, homes, offices, institutions, and public infrastructure."
        />
        <link rel="canonical" href="https://www.aerzenair.com/" />
      </Helmet>

      <Hero />
      <InstitutionalCTA />

      <div className="max-w-7xl mx-auto px-4 mt-16 grid md:grid-cols-2 gap-10">
        <PreorderCounter />
        <LaunchTimeline />
      </div>

      <EarlyAccessCountdown />
      <SocialProof />
      <HealthImpact />
      <GovtAirAdvisory />

      <Suspense fallback={<Loader height={550} />}>
        <DiwaliProtection />
      </Suspense>

      <div className="max-w-5xl mx-auto px-4 mt-16">
        <AppTeaser />
      </div>

      <Products />
      <AQSenseTeaser />
      <ServiceWarranty />
      <Compliance />
      <ManufacturingStory />
      <Features />
      <Testimonials />

      <div className="max-w-5xl mx-auto px-4 mt-16">
        <FAQ />
      </div>

      <InterestForm />
      <Toaster position="top-right" />
    </PageLayout>
  );
}

/* ======================================================
   ROUTES
====================================================== */
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* HOME */}
        <Route path="/" element={<Home />} />

        {/* PRODUCTS / PREBOOK */}
        <Route
          path="/products"
          element={
            <PageLayout>
              <Products />
            </PageLayout>
          }
        />

        <Route
          path="/prebook"
          element={
            <PageLayout>
              <Products />
              <InterestForm />
            </PageLayout>
          }
        />

        <Route
          path="/prebook/checkout"
          element={
            <PageLayout>
              <PrebookCheckout />
            </PageLayout>
          }
        />

        {/* INSTITUTIONAL */}
        <Route
          path="/institutional-solutions"
          element={
            <PageLayout>
              <InstitutionalSolutions />
            </PageLayout>
          }
        />

        <Route
          path="/dealer-enquiry"
          element={
            <PageLayout>
              <DealerEnquiry />
            </PageLayout>
          }
        />

        <Route
          path="/mafa"
          element={
            <PageLayout>
              <MAFA />
            </PageLayout>
          }
        />

        <Route
          path="/initiatives/airshield-transit-zone"
          element={
            <PageLayout>
              <AirShieldTransitZone />
            </PageLayout>
          }
        />

        {/* INFO */}
        <Route
          path="/aq-sense"
          element={
            <PageLayout>
              <AQSense />
            </PageLayout>
          }
        />

        <Route
          path="/vision"
          element={
            <PageLayout>
              <Vision />
            </PageLayout>
          }
        />

        <Route
          path="/mission"
          element={
            <PageLayout>
              <Mission />
            </PageLayout>
          }
        />

        <Route
          path="/environmental-responsibility"
          element={
            <PageLayout>
              <EnvironmentalResponsibility />
            </PageLayout>
          }
        />

        {/* LEGAL */}
        <Route path="/terms" element={<PageLayout><Terms /></PageLayout>} />
        <Route path="/refund" element={<PageLayout><Refund /></PageLayout>} />
        <Route path="/shipping" element={<PageLayout><Shipping /></PageLayout>} />
        <Route path="/privacy" element={<PageLayout><Privacy /></PageLayout>} />
        <Route path="/contact" element={<PageLayout><Contact /></PageLayout>} />
      </Routes>
    </BrowserRouter>
  );
}